import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2, Package, KeyRound, CheckCircle2, Truck, XCircle,
  ShoppingBag, Camera, X, IndianRupee, MapPin, Copy, Share2, Bookmark,
  ArrowLeft,
} from "lucide-react";

// Format ISO to "20 Jul, 5:44 pm" IST — matches the logged-in orders page.
const IST = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch { return iso; }
};

const STATUS_META = {
  pending:      { label: "Order received",  Icon: Package,      cls: "bg-slate-100 text-slate-700" },
  confirmed:    { label: "Confirmed",        Icon: CheckCircle2, cls: "bg-blue-100 text-blue-700" },
  dispatched:   { label: "Out for delivery", Icon: Truck,        cls: "bg-amber-100 text-amber-700" },
  awaiting_otp: { label: "Delivery agent here", Icon: KeyRound,  cls: "bg-fuchsia-100 text-fuchsia-700" },
  delivered:    { label: "Delivered",        Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700" },
  cancelled:    { label: "Cancelled",        Icon: XCircle,      cls: "bg-red-100 text-red-700" },
};

/**
 * Public order-tracking page.
 *
 * Route: /shop/track/:orderId?t=<signed-token>
 * NO login required — the URL is the credential. Meant for customers who
 * placed the order and got the link (via order confirmation email, or from
 * the "Copy tracking link" button on their My Orders page). Also works when
 * their session has expired and the delivery agent is at the door.
 */
export default function ShopTrack() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const isFresh = params.get("fresh") === "1";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [proofPhoto, setProofPhoto] = useState(null);
  const [dismissedBanner, setDismissedBanner] = useState(false);
  // Back from PhonePe (?pp=1): ask the server whether the payment went through.
  const [ppState, setPpState] = useState(params.get("pp") === "1" ? "checking" : null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (ppState !== "checking" || !orderId || !token) return undefined;
    let tries = 0;
    let stop = false;
    const check = async () => {
      try {
        const { data } = await api.post("/store/payments/phonepe/check", { order_id: orderId, t: token });
        if (stop) return;
        // PhonePe can take a few seconds to settle: re-check a few times.
        if (data.payment_status === "pending" && tries++ < 5) return setTimeout(check, 3000);
        setPpState(data.payment_status === "paid" ? "paid" : data.payment_status === "failed" ? "failed" : "pending");
        const { data: fresh } = await api.get(`/store/track/${orderId}?t=${encodeURIComponent(token)}`);
        if (!stop) setOrder(fresh);
      } catch {
        if (!stop) setPpState("pending");
      }
    };
    check();
    return () => { stop = true; };
  }, [ppState, orderId, token]);

  const payWithPhonePe = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/store/payments/phonepe/create", { order_id: orderId, t: token });
      window.location.href = data.redirect_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the payment.");
      setPaying(false);
    }
  };

  const trackUrl = typeof window !== "undefined" ? window.location.href.split("&fresh=")[0] : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(trackUrl);
      toast.success("Link copied — paste it anywhere to save it");
    } catch { toast.error("Copy failed — long-press the address bar instead"); }
  };

  const shareToWhatsApp = () => {
    const shopName = order?.shop?.name || "the shop";
    const msg = `My order from ${shopName} — tap this link anytime to see the delivery PIN, even without logging in:\n\n${trackUrl}`;
    // wa.me works on all devices; opens WhatsApp on mobile & WhatsApp Web on desktop.
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  useEffect(() => {
    if (!orderId || !token) {
      setError("This tracking link is invalid or has been revoked.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(
          `/store/track/${orderId}?t=${encodeURIComponent(token)}`
        );
        if (!cancelled) setOrder(data);
      } catch (err) {
        if (!cancelled) setError(
          err?.response?.data?.detail || "This tracking link is invalid or has been revoked."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Refresh every 15s while awaiting OTP so the code shows up in near-real-time.
    const t = setInterval(() => load(), 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [orderId, token]);

  const refresh = async () => {
    try {
      const { data } = await api.get(
        `/store/track/${orderId}?t=${encodeURIComponent(token)}`
      );
      setOrder(data);
    } catch { /* silent — the interval will retry */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-3">
            <XCircle className="w-8 h-8 text-red-500 mx-auto" />
            <div className="text-lg font-medium">{error}</div>
            <p className="text-sm text-slate-500">
              Ask the shop to resend your tracking link, or sign in to view all your orders.
            </p>
            <Link to="/shop/login">
              <Button className="w-full bg-[#0055FF] hover:bg-[#0040CC]">Sign in</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const shop = order?.shop || {};
  const primary = shop.primary_color || "#0055FF";
  const status = order?.status || "pending";
  const meta = STATUS_META[status] || STATUS_META.pending;
  const StatusIcon = meta.Icon;

  return (
    <div
      className="min-h-screen bg-slate-50"
      style={{ ["--shop-primary"]: primary }}
    >
      {/* Header — carries the shop brand */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          {shop.logo_url ? (
            <img src={shop.logo_url} alt={shop.name} className="w-8 h-8 rounded object-cover" />
          ) : (
            <div
              className="w-8 h-8 rounded flex items-center justify-center"
              style={{ background: primary }}
            >
              <ShoppingBag className="w-4 h-4 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{shop.name || "DialFlow Shop"}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Order tracking</div>
          </div>
          {/* Back to storefront — lets the customer keep shopping instead of
              being stranded on the tracking page after checkout. The tenant
              resolver picks the shop up from the current subdomain, so `/shop`
              lands them back on their own storefront. */}
          <Link
            to="/shop"
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md text-white hover:opacity-90 shrink-0"
            style={{ background: primary }}
            data-testid="track-continue-shopping"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Continue shopping</span>
            <span className="sm:hidden">Shop</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {(ppState || (order?.payment_gateway === "phonepe" && order?.payment_method !== "cod")) && (() => {
          const paid = order?.payment_status === "paid" || ppState === "paid";
          const failed = !paid && (ppState === "failed" || order?.payment_status === "failed");
          const waiting = !paid && !failed;
          if (paid && !ppState) return null;            // nothing to say once it's settled
          return (
            <Card
              className={`border-2 ${paid ? "border-emerald-500" : failed ? "border-red-400" : "border-amber-400"}`}
              data-testid="track-payment-status"
            >
              <CardContent className="p-4 space-y-2">
                <div className={`font-medium ${paid ? "text-emerald-700" : failed ? "text-red-600" : "text-amber-700"}`}>
                  {ppState === "checking"
                    ? "Checking your payment with PhonePe…"
                    : paid
                      ? "Payment received. Thank you! Your order is confirmed."
                      : failed
                        ? "The payment didn't go through. No money was taken, or it will be returned by your bank."
                        : "Your payment hasn't been confirmed yet. If money was taken, it will show here within a few minutes."}
                </div>
                {!paid && ppState !== "checking" && (
                  <Button
                    onClick={payWithPhonePe}
                    disabled={paying}
                    style={{ background: primary }}
                    className="text-white"
                    data-testid="track-pay-again"
                  >
                    {paying ? "Opening PhonePe…" : waiting ? "Pay now with PhonePe" : "Try paying again"}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })()}
        {/* Save-your-link banner. Shows prominently right after order
            placement (`?fresh=1`) and can be dismissed after that. This is
            the customer's only chance to grab the tracking URL before they
            log out and lose easy access to the delivery PIN. */}
        {isFresh && !dismissedBanner && (
          <Card
            className="border-2"
            style={{ borderColor: primary }}
            data-testid="track-save-banner"
          >
            <CardContent
              className="p-4 space-y-3 relative"
              style={{ background: `${primary}0F` }}
            >
              <button
                type="button"
                onClick={() => setDismissedBanner(true)}
                className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
                aria-label="Dismiss"
                data-testid="track-save-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2" style={{ color: primary }}>
                <Bookmark className="w-4 h-4" />
                <span className="mono text-[10px] uppercase tracking-widest font-semibold">
                  Order placed · Save this link
                </span>
              </div>
              <p className="text-sm text-slate-700 leading-snug">
                <b>Bookmark this page or share the link to yourself now.</b> You
                can open it anytime — even after logging out — to see live
                delivery status and read out your PIN to the delivery agent.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  onClick={copyLink}
                  className="flex-1 gap-2"
                  style={{ background: primary, color: "#fff" }}
                  data-testid="track-copy-link"
                >
                  <Copy className="w-4 h-4" /> Copy link
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={shareToWhatsApp}
                  className="flex-1 gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="track-share-whatsapp"
                >
                  <Share2 className="w-4 h-4" /> Send to WhatsApp
                </Button>
              </div>
              <p className="text-[11px] text-slate-500">
                Tip: On phones, tap the address bar → <b>Share → Add to Home Screen</b> so this order sits one tap away.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-between">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-slate-500">
              Order · {IST(order?.created_at)}
            </div>
            <div className="text-sm text-slate-700 mono">#{orderId.slice(0, 8).toUpperCase()}</div>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${meta.cls}`} data-testid="track-status">
            <StatusIcon className="w-3.5 h-3.5" />
            {meta.label}
          </span>
        </div>

        {/* OTP card — only visible while awaiting_otp */}
        {status === "awaiting_otp" && order?.delivery_otp && (
          <Card
            className="border-2"
            style={{ borderColor: "rgba(217, 70, 239, 0.35)" }}
            data-testid="track-otp-card"
          >
            <CardContent className="p-5 space-y-2 bg-fuchsia-50/50">
              <div className="flex items-center gap-2 text-fuchsia-800">
                <KeyRound className="w-4 h-4" />
                <span className="mono text-[10px] uppercase tracking-widest font-semibold">
                  Delivery code — read it out to the delivery person
                </span>
              </div>
              <div
                className="mono text-5xl font-extrabold tracking-[0.4em] text-fuchsia-800 text-center py-3"
                data-testid="track-otp"
              >
                {order.delivery_otp.split("").join(" ")}
              </div>
              <Button
                variant="outline"
                className="w-full border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100"
                onClick={() => {
                  navigator.clipboard?.writeText(order.delivery_otp);
                  toast.success("Code copied");
                }}
                data-testid="track-otp-copy"
              >
                Copy code
              </Button>
              <p className="text-[11px] text-fuchsia-700/80 text-center">
                Your delivery person has arrived. This page updates every 15 seconds — refresh if the code stops working.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Doorstep proof — surfaces once the delivery is completed */}
        {status === "delivered" && order?.delivery_proof_photo && (
          <button
            type="button"
            onClick={() => setProofPhoto(order.delivery_proof_photo)}
            className="w-full rounded-lg overflow-hidden border border-emerald-200 relative focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="track-proof"
          >
            <img
              src={order.delivery_proof_photo}
              alt="Doorstep delivery proof"
              className="w-full h-56 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 via-transparent to-transparent flex items-end justify-between p-3">
              <span className="mono text-[10px] uppercase tracking-widest text-white bg-emerald-600 px-2 py-0.5 rounded shadow flex items-center gap-1">
                <Camera className="w-3 h-3" /> Doorstep proof
              </span>
              <span className="text-white/90 text-[11px]">Tap to zoom</span>
            </div>
          </button>
        )}

        {/* Order summary */}
        <Card>
          <CardContent className="p-4">
            <div className="mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Items</div>
            <ul className="space-y-1.5">
              {(order?.items || []).map((it, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 truncate pr-2">{it.name}</span>
                  <span className="mono text-slate-500 shrink-0">
                    ×{it.quantity} · ₹{Number(it.price).toLocaleString("en-IN")}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-slate-100 text-sm space-y-1">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span className="mono">₹{Number(order?.subtotal || 0).toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Shipping</span>
                <span className="mono">
                  {order?.shipping_charge > 0
                    ? `₹${Number(order.shipping_charge).toLocaleString("en-IN")}`
                    : "Free"}
                </span>
              </div>
              <div className="flex justify-between font-semibold text-slate-800 pt-1">
                <span>Total</span>
                <span className="mono flex items-center">
                  <IndianRupee className="w-3.5 h-3.5" />
                  {Number(order?.total || 0).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {order?.delivery_address && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <div className="mono text-[11px] uppercase tracking-widest text-slate-500">Delivering to</div>
                  <div className="text-slate-700">{order.delivery_address}</div>
                  {order.pin_code && (
                    <div className="mono text-[11px] text-slate-500 mt-0.5">PIN {order.pin_code}</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[11px] text-slate-400 py-2">
          Bookmark this link to check your order anytime — no login needed.
        </p>

        {/* Big bottom CTA — after a long checkout flow customers instinctively
            look at the bottom of the page for the "what next" action. */}
        <Link
          to="/shop"
          className="block w-full text-center py-4 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
          style={{ background: primary }}
          data-testid="track-continue-shopping-bottom"
        >
          Continue shopping →
        </Link>
      </main>

      {/* Zoom overlay for doorstep proof */}
      {proofPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setProofPhoto(null)}
          data-testid="track-proof-overlay"
        >
          <button
            type="button"
            onClick={() => setProofPhoto(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={proofPhoto}
            alt="Doorstep delivery proof"
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
